/**
 * Bill-wise allocation (Alt+B).
 *
 * When a receipt is entered against a customer, this is where the user
 * says WHICH invoices it settles. Without it, a trading business knows its
 * customer owes 45,000 but not which of eleven invoices are overdue --
 * which is the difference between a ledger and a working receivables
 * process.
 *
 * "Auto-allocate" applies the amount oldest-bill-first, which is what an
 * accounts clerk does by hand and is the correct default under most
 * credit terms.
 */
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, Skeleton, Stack, TextField, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { fetchPartyBills } from '../api/endpoints'
import type { BillAllocationIn, OpenBill } from '../api/types'
import { c } from '../theme'
import { addMoney, display, sanitize, subMoney, normalise } from './money'

interface Props {
  open: boolean
  ledgerCode: string
  ledgerName: string
  /** The line amount being allocated. */
  amount: string
  /** NEW when this voucher creates a bill (an invoice), AGAINST otherwise. */
  mode: 'NEW' | 'AGAINST'
  initial: BillAllocationIn[]
  onClose: () => void
  onApply: (allocations: BillAllocationIn[]) => void
}

export default function BillAllocationDialog({
  open, ledgerCode, ledgerName, amount, mode, initial, onClose, onApply,
}: Props) {
  const { t } = useTranslation()
  const [alloc, setAlloc] = useState<Record<string, string>>({})
  const [newRef, setNewRef] = useState('')
  const [dueDate, setDueDate] = useState('')

  const q = useQuery({
    enabled: open && mode === 'AGAINST' && !!ledgerCode,
    queryKey: ['party-bills', ledgerCode],
    queryFn: () => fetchPartyBills(ledgerCode),
  })

  useEffect(() => {
    if (!open) return
    const seed: Record<string, string> = {}
    for (const a of initial) seed[a.ref_name] = a.amount
    setAlloc(seed)
    if (mode === 'NEW') setNewRef(initial[0]?.ref_name ?? '')
  }, [open, initial, mode])

  const bills = q.data?.bills ?? []

  const allocated = useMemo(
    () => addMoney(...Object.values(alloc).filter(Boolean)),
    [alloc])
  const remaining = subMoney(amount || '0', allocated)
  const exact = normalise(remaining) === '0.00'

  function autoAllocate() {
    let left = amount || '0'
    const next: Record<string, string> = {}
    // Oldest first: bills arrive sorted by due date from the server.
    for (const b of bills) {
      if (normalise(left) === '0.00') break
      if (b.is_advance) continue
      const take = Number(left) >= Number(b.outstanding) ? b.outstanding : left
      next[b.ref_name] = normalise(take)
      left = subMoney(left, take)
    }
    setAlloc(next)
  }

  function apply() {
    if (mode === 'NEW') {
      onApply(newRef.trim() ? [{
        ref_type: 'NEW', ref_name: newRef.trim(), amount,
        due_date: dueDate || null,
      }] : [])
    } else {
      onApply(Object.entries(alloc)
        .filter(([, v]) => v && normalise(v) !== '0.00')
        .map(([ref_name, v]) => ({
          ref_type: 'AGAINST' as const, ref_name, amount: normalise(v),
        })))
    }
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {t('bills.title')}
        <Typography variant="body2" color="text.secondary">
          {ledgerName} · <span className="num">{display(amount || '0')}</span>
        </Typography>
      </DialogTitle>

      <DialogContent>
        {mode === 'NEW' ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ fontSize: 13 }}>
              {t('bills.newHint')}
            </Alert>
            <TextField label={t('bills.reference')} value={newRef} autoFocus
              onChange={(e) => setNewRef(e.target.value)}
              helperText={t('bills.referenceHint')} />
            <TextField type="date" label={t('bills.dueDate')} value={dueDate}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setDueDate(e.target.value)}
              helperText={t('bills.dueHint')} sx={{ width: 200 }} />
          </Stack>
        ) : q.isLoading ? (
          <Box sx={{ py: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
        ) : bills.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>{t('bills.noOpenBills')}</Alert>
        ) : (
          <>
            <Stack direction="row" alignItems="center" spacing={1}
              sx={{ mt: 1, mb: 1 }}>
              <Button size="small" onClick={autoAllocate}>
                {t('bills.autoAllocate')}
              </Button>
              <Button size="small" sx={{ color: c.inkMuted }}
                onClick={() => setAlloc({})}>{t('bills.clear')}</Button>
            </Stack>

            <Box sx={{
              display: 'grid', gridTemplateColumns: '1fr 96px 100px 108px',
              gap: 1, px: 1, pb: 0.5 }}>
              <span className="section-label">{t('bills.reference')}</span>
              <span className="section-label" style={{ textAlign: 'end' }}>
                {t('bills.due')}</span>
              <span className="section-label" style={{ textAlign: 'end' }}>
                {t('bills.outstanding')}</span>
              <span className="section-label" style={{ textAlign: 'end' }}>
                {t('bills.allocate')}</span>
            </Box>

            <Box className="ruled" sx={{
              maxHeight: 300, overflowY: 'auto',
              border: `1px solid ${c.border}`, borderRadius: 1 }}>
              {bills.map((b: OpenBill) => (
                <Box key={b.ref_name} sx={{
                  display: 'grid', gridTemplateColumns: '1fr 96px 100px 108px',
                  gap: 1, alignItems: 'center', px: 1, py: 0.75 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13 }} noWrap>{b.ref_name}</Typography>
                    {b.days_overdue > 0 && (
                      <Typography sx={{ fontSize: 10.5, color: c.dangerInk }}>
                        {t('bills.daysOverdue', { n: b.days_overdue })}
                      </Typography>
                    )}
                  </Box>
                  <span className="num" style={{ fontSize: 11.5, color: c.inkMuted }}>
                    {b.due_date ?? b.bill_date}</span>
                  <span className="num" style={{ fontSize: 12.5 }}>
                    {display(b.outstanding)}</span>
                  <input className="num" inputMode="decimal"
                    value={alloc[b.ref_name] ?? ''}
                    onChange={(e) => setAlloc({
                      ...alloc, [b.ref_name]: sanitize(e.target.value) })}
                    onFocus={(e) => e.currentTarget.select()}
                    style={{
                      width: '100%', height: 28, padding: '0 6px',
                      border: `1px solid ${c.border}`, borderRadius: 4,
                      font: 'inherit', fontFamily: 'var(--mono)',
                      textAlign: 'right', outline: 'none',
                    }} />
                </Box>
              ))}
            </Box>

            <Stack direction="row" justifyContent="space-between"
              sx={{ mt: 1.5, px: 1 }}>
              <Typography sx={{ fontSize: 13, color: c.inkMuted }}>
                {t('bills.unallocated')}
              </Typography>
              <span className={`num ${exact ? 'num-ok' : 'num-bad'}`}
                style={{ fontWeight: 600 }}>{display(remaining)}</span>
            </Stack>
            {!exact && (
              <Typography variant="caption" sx={{ color: c.inkMuted, px: 1 }}>
                {t('bills.unallocatedHint')}
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={apply}>{t('common.save')}</Button>
      </DialogActions>
    </Dialog>
  )
}
